import { reverse, uniq } from 'ramda';
import type { Location, Node } from 'slate';
import { Editor, Text, Transforms } from 'slate';

import {
  AlineTransforms,
  EditingMode,
  isDeleteEdit,
  isEditWithSource,
  isInsertEdit,
  isParagraphElement,
  isTableCellElement,
  isTableRowElement,
  Queries,
  setGlyphSuggestedEdits,
} from '../internal';
import type {
  AlineText,
  ParagraphElement,
  SuggestionSource,
  TableCellElement,
  TableRowElement,
} from '../internal';

type SuggestedTextEdit = NonNullable<AlineText['suggestedEdits']>[number];
type SourceRemovalEdit = {
  edit: SuggestedTextEdit;
  removedSource: boolean;
};

/**
 * Matches nodes that carry a suggested edit with a matching source. Sourced
 * edits live on text nodes (insertNode/deleteNode), on table rows
 * (insertTableRow/removeTableRow, for AI-inserted tables and row removals),
 * and on table cells (removeTableCell, for AI column removals) — all must
 * participate in source-based accept/reject so resolving an AI table
 * suggestion handles its structure, not just its text.
 */
function getIsNodeWithMatchingSourcedEdit(
  node: Node,
  authorId: string,
  sourceMatch: (source: SuggestionSource) => boolean
): node is AlineText | TableRowElement | TableCellElement {
  if (!Text.isText(node) && !isTableRowElement(node) && !isTableCellElement(node)) return false;
  return Boolean(
    node.suggestedEdits?.some(
      edit => edit.authorId === authorId && isEditWithSource(edit) && sourceMatch(edit.source)
    )
  );
}

export const SuggestionWithSourceTransforms = {
  /**
   * Accept all suggested edits at a location that have a source.
   */
  acceptSuggestionsWithSource: (
    editor: Editor,
    options: {
      at: Location;
      authorId: string;
      editingMode: EditingMode;
      sourceMatch?: (source: SuggestionSource) => boolean;
    }
  ): SuggestionSource[] => {
    return AlineTransforms.withoutNormalizing<SuggestionSource[]>(editor, () => {
      const { at, authorId, editingMode, sourceMatch = () => true } = options;

      if (editingMode === EditingMode.VIEWING || editingMode === EditingMode.COMMENTING) return [];

      removeOrphanedEmptyTextDeleteEdits(editor);

      const editEntriesWithSource = Queries.sortedEditRanges(editor, {
        at,
        match: node => getIsNodeWithMatchingSourcedEdit(node, authorId, sourceMatch),
      }).filter(
        entry =>
          entry.edit.authorId === authorId &&
          isEditWithSource(entry.edit) &&
          sourceMatch(entry.edit.source)
      );

      AlineTransforms.acceptSuggestedEdits(editor, {
        authorId,
        editEntries: editEntriesWithSource,
        editingMode: EditingMode.EDITING,
      });

      return uniq(
        editEntriesWithSource.flatMap(({ edit }) => {
          return isEditWithSource(edit) ? [edit.source] : [];
        })
      );
    });
  },

  /**
   * Keep ("redline") the sourced suggestions the current selection touches,
   * detaching them from their AI suggestion group so they behave like
   * user-made suggested edits.
   *
   * Scoped the same way the inline toolbar's accept/reject are: the affected
   * set is the edit entries (contiguous runs of a single suggested edit)
   * whose range intersects the selection. A partial overlap keeps the whole
   * entry; entries of the same group outside the selection stay sourced.
   * Only the current user's sourced edits participate.
   */
  keepSelectedSuggestions: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
    }
  ): SuggestionSource[] => {
    const { authorId, editingMode } = options;
    if (editingMode === EditingMode.VIEWING || editingMode === EditingMode.COMMENTING) return [];
    if (!editor.selection) return [];

    const allEntries = Queries.sortedEditRanges(editor);
    const selectedEntries = Queries.selectedEdits(editor, allEntries);
    const keepTargets = selectedEntries.flatMap(entry =>
      entry.edit.authorId === authorId && isEditWithSource(entry.edit)
        ? [{ groupId: entry.edit.source.groupId, range: entry.range }]
        : []
    );

    return AlineTransforms.withoutNormalizing<SuggestionSource[]>(editor, () => {
      const sources: SuggestionSource[] = [];

      // Bottom-up so a node removal in one entry cannot shift a later entry's paths.
      for (const { groupId, range } of reverse(keepTargets)) {
        sources.push(
          ...SuggestionWithSourceTransforms.removeSourceFromSuggestions(editor, {
            at: range,
            authorId,
            editingMode,
            sourceMatch: source => source.groupId === groupId,
          })
        );
      }

      return uniq(sources);
    });
  },

  /**
   * Reject all suggested edits at a location that have a source.
   */
  rejectSuggestionsWithSource: (
    editor: Editor,
    options: {
      at: Location;
      authorId: string;
      editingMode: EditingMode;
      sourceMatch?: (source: SuggestionSource) => boolean;
    }
  ): SuggestionSource[] => {
    return AlineTransforms.withoutNormalizing<SuggestionSource[]>(editor, () => {
      const { at, authorId, editingMode, sourceMatch = () => true } = options;

      if (editingMode === EditingMode.VIEWING || editingMode === EditingMode.COMMENTING) return [];

      removeOrphanedEmptyTextDeleteEdits(editor);

      const editEntriesWithSource = Queries.sortedEditRanges(editor, {
        at,
        match: node => getIsNodeWithMatchingSourcedEdit(node, authorId, sourceMatch),
      }).filter(
        entry =>
          entry.edit.authorId === authorId &&
          isEditWithSource(entry.edit) &&
          sourceMatch(entry.edit.source)
      );

      AlineTransforms.rejectSuggestedEdits(editor, {
        authorId,
        editEntries: editEntriesWithSource,
        editingMode,
      });

      return uniq(
        editEntriesWithSource.flatMap(({ edit }) => {
          return isEditWithSource(edit) ? [edit.source] : [];
        })
      );
    });
  },

  /**
   * Remove the source from all suggested edits at a location.
   *
   * This means that the suggested edits will be turned into normal suggested
   * edits as if a user added them into the document.
   */
  removeSourceFromSuggestions: (
    editor: Editor,
    options: {
      at: Location;
      authorId: string;
      editingMode: EditingMode;
      sourceMatch?: (source: SuggestionSource) => boolean;
    }
  ): SuggestionSource[] => {
    return AlineTransforms.withoutNormalizing<SuggestionSource[]>(editor, () => {
      const { at, authorId, editingMode, sourceMatch = () => true } = options;

      if (editingMode === EditingMode.VIEWING || editingMode === EditingMode.COMMENTING) return [];

      removeOrphanedEmptyTextDeleteEdits(editor);

      const nodeEntries = [
        ...Editor.nodes<AlineText>(editor, {
          at,
          match: node =>
            Text.isText(node) &&
            !!node.suggestedEdits?.some(
              edit =>
                edit.authorId === authorId && isEditWithSource(edit) && sourceMatch(edit.source)
            ),
          voids: true,
        }),
      ];
      const sources: SuggestionSource[] = [];

      for (const [node, path] of reverse(nodeEntries)) {
        sources.push(
          ...(node.suggestedEdits?.flatMap(edit =>
            edit.authorId === authorId && isEditWithSource(edit) && sourceMatch(edit.source)
              ? [edit.source]
              : []
          ) ?? [])
        );
        const editsWithSourceRemoval =
          node.suggestedEdits?.map(suggestedEdit => {
            const removedSource =
              suggestedEdit.authorId === authorId &&
              isEditWithSource(suggestedEdit) &&
              sourceMatch(suggestedEdit.source);
            return {
              edit: removedSource ? { ...suggestedEdit, source: undefined } : suggestedEdit,
              removedSource,
            };
          }) ?? [];
        const { nextSuggestedEdits, shouldRemoveNode } = removeOlderConflictingTextEdits(
          editsWithSourceRemoval,
          authorId
        );

        if (shouldRemoveNode) {
          Transforms.removeNodes(editor, { at: path, voids: true });
          continue;
        }

        Transforms.setNodes(
          editor,
          { suggestedEdits: nextSuggestedEdits },
          { at: path, voids: true }
        );
      }

      /**
       * Table rows carry sourced `insertTableRow` edits when the table was
       * inserted by an AI suggestion. Strip their source too so the kept
       * insertion behaves like a user-made one (no text-edit conflict logic
       * applies to row edits).
       */
      const rowEntries = [
        ...Editor.nodes<TableRowElement>(editor, {
          at,
          match: node =>
            isTableRowElement(node) &&
            getIsNodeWithMatchingSourcedEdit(node, authorId, sourceMatch),
          voids: true,
        }),
      ];
      for (const [row, rowPath] of reverse(rowEntries)) {
        const edits = row.suggestedEdits ?? [];
        const nextRowEdits = edits.map(edit => {
          if (!(edit.authorId === authorId && isEditWithSource(edit) && sourceMatch(edit.source)))
            return edit;
          sources.push(edit.source);
          return { ...edit, source: undefined };
        });
        Transforms.setNodes(editor, { suggestedEdits: nextRowEdits }, { at: rowPath, voids: true });
      }

      /**
       * Table cells carry sourced `removeTableCell` edits when a column
       * removal was suggested by AI. Strip their source too so the kept
       * removal behaves like a user-made one instead of staying attached to
       * its chat suggestion group.
       */
      const cellEntries = [
        ...Editor.nodes<TableCellElement>(editor, {
          at,
          match: node =>
            isTableCellElement(node) &&
            getIsNodeWithMatchingSourcedEdit(node, authorId, sourceMatch),
          voids: true,
        }),
      ];
      for (const [cell, cellPath] of reverse(cellEntries)) {
        const edits = cell.suggestedEdits ?? [];
        const nextCellEdits = edits.map(edit => {
          if (!(edit.authorId === authorId && isEditWithSource(edit) && sourceMatch(edit.source)))
            return edit;
          sources.push(edit.source);
          return { ...edit, source: undefined };
        });
        Transforms.setNodes(
          editor,
          { suggestedEdits: nextCellEdits },
          { at: cellPath, voids: true }
        );
      }

      /**
       * Paragraph split/merge suggestions live on `glyphProps.suggestedEdits`,
       * not on text nodes — strip their sources too so a "kept" paragraph-mark
       * suggestion fully detaches from its AI group like the text edits above.
       */
      const paragraphEntries = Editor.nodes<ParagraphElement>(editor, {
        at,
        match: node =>
          isParagraphElement(node) &&
          Boolean(
            node.glyphProps?.suggestedEdits?.some(
              edit =>
                edit.authorId === authorId && isEditWithSource(edit) && sourceMatch(edit.source)
            )
          ),
        voids: true,
      });
      for (const [paragraph, paragraphPath] of paragraphEntries) {
        const glyphEdits = paragraph.glyphProps?.suggestedEdits ?? [];
        sources.push(
          ...glyphEdits.flatMap(edit =>
            edit.authorId === authorId && isEditWithSource(edit) && sourceMatch(edit.source)
              ? [edit.source]
              : []
          )
        );
        setGlyphSuggestedEdits(
          editor,
          paragraphPath,
          glyphEdits.map(edit =>
            edit.authorId === authorId && isEditWithSource(edit) && sourceMatch(edit.source)
              ? { ...edit, source: undefined }
              : edit
          )
        );
      }

      return uniq(sources);
    });
  },
};

function removeOlderConflictingTextEdits(edits: SourceRemovalEdit[], authorId: string) {
  const nextEdits: SuggestedTextEdit[] = [];
  const keptEditTypes = new Set<'deleteNode' | 'insertNode'>();
  let shouldRemoveNode = false;

  for (const { edit, removedSource } of edits) {
    if (getDoesConflictWithKeptEdit(edit, keptEditTypes)) {
      /**
       * Same-author insert/delete conflicts are best treated as an undo. This
       * keeps the redline view focused on meaningful counterparty changes
       * instead of showing a user deleting text they previously added.
       */
      if (isInsertEdit(edit) && edit.authorId === authorId && keptEditTypes.has('deleteNode'))
        shouldRemoveNode = true;

      continue;
    }

    nextEdits.push(edit);
    if (!removedSource) continue;

    if (isDeleteEdit(edit)) keptEditTypes.add('deleteNode');
    if (isInsertEdit(edit)) keptEditTypes.add('insertNode');
  }

  return { nextSuggestedEdits: nextEdits, shouldRemoveNode };
}

function getDoesConflictWithKeptEdit(
  edit: SuggestedTextEdit,
  keptEditTypes: Set<'deleteNode' | 'insertNode'>
) {
  if (isEditWithSource(edit)) return false;

  return (
    (isDeleteEdit(edit) && keptEditTypes.has('insertNode')) ||
    (isInsertEdit(edit) && keptEditTypes.has('deleteNode'))
  );
}

/**
 * Removes `deleteNode` edits from empty text nodes anywhere in the document.
 *
 * A deletion mark on zero characters is meaningless (there is nothing to
 * delete) and unresolvable — accept/reject process edits by text range, and a
 * zero-width mark yields a collapsed range their sweeps skip — so such marks
 * accumulate as invisible pending suggestions that keep the suggestions
 * toolbar alive with nothing to show or act on. Running this at the start of
 * every source-based resolution both prevents interference with the current
 * action and heals documents already stuck in that state the next time
 * anyone handles a suggestion in them. Only `deleteNode` is stripped:
 * `insertNode` on an empty leaf is meaningful (it ties a suggested empty
 * paragraph to its suggestion so rejecting removes it).
 */
function removeOrphanedEmptyTextDeleteEdits(editor: Editor): void {
  const orphanEntries = [
    ...Editor.nodes<AlineText>(editor, {
      at: [],
      match: node =>
        Text.isText(node) &&
        node.text === '' &&
        Boolean(node.suggestedEdits?.some(edit => edit.type === 'deleteNode')),
      voids: true,
    }),
  ];
  for (const [node, path] of reverse(orphanEntries)) {
    const nextSuggestedEdits = (node.suggestedEdits ?? []).filter(
      edit => edit.type !== 'deleteNode'
    );
    Transforms.setNodes(editor, { suggestedEdits: nextSuggestedEdits }, { at: path, voids: true });
  }
}
